import { AxiosInstance } from "axios";
import { User } from "../types/User";

export interface Role {
  id: number;
  label: string;
}

export const userService = {
  getAllUsers: async (api: AxiosInstance): Promise<User[]> => {
    try {
      const response = await api.get('/user-management/users');
      return response.data.map((user: any) => ({
        id: user.userId,
        nom: user.lastName,
        prenom:user.firstName,
        telephone:user.phoneNumber,
        email:user.email,
        role:user.role.roleLabel,
        adresse:user.address,
        username:user.username,
        password:user.password,
        createdAt:user.createdAt,  
        statut: user.isActive?"actif":"inactif"
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des Users:', error);
      throw error;
    }
  },

  getUserById: async (api: AxiosInstance, userId: string | number): Promise<User> => {
    try {
      const response = await api.get(`/user-management/users/${userId}`);
      const user = response.data;
      
      return {
        id: user.userId,
        nom: user.lastName,
        prenom:user.firstName,
        telephone:user.phoneNumber,
        email:user.email,
        role:user.role.roleLabel,
        adresse:user.address,
        username:user.username,
        password:user.password,
        createdAt:user.createdAt,  
        statut: user.isActive?"actif":"inactif"
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'utilisateur ${userId}:`, error);
      throw error;
    }
  },
  
  createUser: async (
    api: AxiosInstance,
    newUser: any,
    creatorEmail?: string
  ): Promise<any> => {
    try {
      const response = await api.post(
        `/user-management/register${creatorEmail ? `?creatorEmail=${creatorEmail}` : ""}`,
        newUser
      );

      const user = response.data;

      return {
        id: user.userId,
        nom: user.lastName,
        prenom: user.firstName,
        telephone: user.phoneNumber,
        email: user.email,
        role: user.role?.roleLabel,
        adresse: user.address,
        username: user.username,
        password: user.password,
        createdAt: user.createdAt,
        statut: user.isActive ? "actif" : "inactif",
      };

    } catch (error: any) {
      console.error("Erreur lors de la création de l'utilisateur :", error);

      if (error.response?.status === 400) {
        const code = error.response.data;

        if (code === "email.taken") {
          throw new Error("EMAIL_TAKEN");
        }

        if (code === "username.taken") {
          throw new Error("USERNAME_TAKEN");
        }

        throw new Error("BAD_REQUEST");
      }

      if (error.response?.status === 500) {
        throw new Error("SERVER_ERROR");
      }

      throw new Error("UNKNOWN_ERROR");
    }
  },

  updateUser: async (api: AxiosInstance, userId: string | number, updatedUser: any): Promise<any> => {
    try {
      const response = await api.put(`/user-management/users/${userId}`, updatedUser);
      const user = response.data;

      return {
        id: user.userId,
        nom: user.lastName,
        prenom: user.firstName,
        telephone: user.phoneNumber,
        email: user.email,
        role: user.role?.roleLabel,
        adresse: user.address,
        username: user.username,
        password: user.password,
        createdAt: user.createdAt,
        statut: user.isActive ? "actif" : "inactif",
      };

    } catch (error: any) {
      console.error("Erreur lors de la mise à jour de l'utilisateur :", error);

      if (error.response?.status === 400) {
        throw new Error(
          error.response.data || 
          "Erreur de validation lors de la mise à jour."
        );
      }

      if (error.response?.status === 500) {
        throw new Error(
          error.response.data || 
          "Erreur serveur lors de la mise à jour de l'utilisateur."
        );
      }

      throw error;
    }
  },

  archiveUser: async (api: AxiosInstance, userId: number): Promise<any> => {
    try {
      const response = await api.put(`/user-management/users/${userId}/archive`);
      return response.data;
    } catch (error: any) {
      console.error(`Erreur lors de l'archivage de l'utilisateur ${userId}:`, error);

      if (error.response?.status === 500) {
        throw new Error(
          error.response.data || 
          "Une erreur est survenue lors de l'archivage de l'utilisateur."
        );
      }

      throw error;
    }
  },

  unarchiveUser: async (api: AxiosInstance, userId: number): Promise<any> => {
    try {
      const response = await api.put(`/user-management/users/${userId}/unarchive`);
      return response.data;
    } catch (error: any) {
      console.error(`Erreur lors du désarchivage de l'utilisateur ${userId}:`, error);

      if (error.response?.status === 500) {
        throw new Error(
          error.response.data ||
          "Une erreur est survenue lors du désarchivage de l'utilisateur."
        );
      }

      throw error;
    }
  },

  updatePassword: async (api: AxiosInstance, email: string, newPassword: string): Promise<string> => {
    try {
      const response = await api.patch(`/user-management/update-password`, null, {
        params: { email, newPassword }
      });
      return response.data as string;
    } catch (error: any) {
      console.error("Erreur lors de la mise à jour du mot de passe :", error);
      throw error.response?.data || error;
    }
  },
};